import { memo } from "react";
import { Alert, Box, Stack } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import type { DocumentReference, Patient } from "fhir/r5";
import {
  UnderlineTab,
  UnderlineTabs,
} from "src-next/shared/ui/navigation/UnderlineTabs";
import {
  ReleaseProductsSection,
  type DocumentCategory,
} from "src-next/modules/ecommerce";
import type {
  PurchaseReleaseLineInput,
  WooProduct,
} from "src-next/modules/patient-supporting-documents";
import DocumentsChecklistSkeleton from "../components/manage/DocumentsChecklistSkeleton";
import { DocumentsSection } from "./DocumentsSection";

interface ManageDocumentsBodyProps {
  step: 0 | 1;
  onStepChange: (step: 0 | 1) => void;
  docsLoading: boolean;
  docsRefreshing: boolean;
  categories: DocumentCategory[];
  organizationId?: string;
  practitionerRoleId?: string;
  transactionId?: string;
  patient: Patient;
  interactionDisabled: boolean;
  docsError: boolean;
  forbidden: boolean;
  missingCpf: boolean;
  onRefetch: () => void;
  onSendWhatsApp?: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
  onView: (doc: DocumentReference, title: string) => void;
  canRelease: boolean;
  hasRejected: boolean;
  storeError: string | null;
  storeReady: boolean;
  products: WooProduct[];
  productsLoading: boolean;
  productsError: string | null;
  lines: PurchaseReleaseLineInput[];
  onAddProduct: (product: WooProduct) => void;
  onUpdateQuantity: (productId: number, quantity: number) => void;
}

export const ManageDocumentsBody = memo(function ManageDocumentsBody({
  step,
  onStepChange,
  docsLoading,
  docsRefreshing,
  categories,
  organizationId,
  practitionerRoleId,
  transactionId,
  patient,
  interactionDisabled,
  docsError,
  forbidden,
  missingCpf,
  onRefetch,
  onSendWhatsApp,
  onView,
  canRelease,
  hasRejected,
  storeError,
  storeReady,
  products,
  productsLoading,
  productsError,
  lines,
  onAddProduct,
  onUpdateQuantity,
}: ManageDocumentsBodyProps) {
  if (docsLoading) {
    return <DocumentsChecklistSkeleton />;
  }

  return (
    <>
      {docsRefreshing && (
        <Alert severity="info" sx={{ flexShrink: 0, mb: 1 }}>
          Atualizando documentos do paciente… As ações de verificação ficam
          temporariamente desabilitadas.
        </Alert>
      )}

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
          mb: 1.5,
        }}
      >
        <UnderlineTabs
          value={step}
          onChange={(_, value: 0 | 1) => onStepChange(value)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ flex: 1, width: "auto" }}
        >
          <UnderlineTab
            value={0}
            icon={<DescriptionOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Verificar documentos"
          />
          <UnderlineTab
            value={1}
            icon={<LocalPharmacyOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Liberar compra"
            disabled={!canRelease}
            title={
              !canRelease
                ? "Verifique ou recuse os documentos pendentes primeiro"
                : undefined
            }
            sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
          />
        </UnderlineTabs>
      </Stack>

      {step === 0 && (
        <DocumentsSection
          categories={categories}
          organizationId={organizationId}
          practitionerRoleId={practitionerRoleId}
          transactionId={transactionId}
          patient={patient}
          interactionDisabled={interactionDisabled}
          error={docsError}
          forbidden={forbidden}
          missingCpf={missingCpf}
          onRefetch={onRefetch}
          onSendWhatsApp={onSendWhatsApp}
          onView={onView}
        />
      )}

      {step === 1 && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {hasRejected && (
            <Alert severity="warning" sx={{ flexShrink: 0, mb: 1 }}>
              Um ou mais documentos foram recusados nesta loja. A liberação de
              compra continua disponível, mas o paciente pode precisar reenviar
              os documentos recusados.
            </Alert>
          )}

          {storeError && (
            <Alert severity="warning" sx={{ flexShrink: 0, mb: 1 }}>
              {storeError}
            </Alert>
          )}

          {storeReady && (
            <ReleaseProductsSection
              products={products}
              productsLoading={productsLoading}
              productsError={productsError}
              lines={lines}
              onAddProduct={onAddProduct}
              onUpdateQuantity={onUpdateQuantity}
            />
          )}
        </Box>
      )}
    </>
  );
});
